import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ComputerIcon from '@mui/icons-material/Computer';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import LanguageIcon from '@mui/icons-material/Language';
import RefreshIcon from '@mui/icons-material/Refresh';
import SavingsIcon from '@mui/icons-material/Savings';
import StorageIcon from '@mui/icons-material/Storage';
import SubtitlesIcon from '@mui/icons-material/Subtitles';
import TranslateIcon from '@mui/icons-material/Translate';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import {
    Alert,
    Box,
    Button,
    ButtonGroup,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    LinearProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { ReportService } from '../../services/ReportService';
import AdminPageWrapper from './AdminPageWrapper';

// ─── Timeline Card ──────────────────────────────────────────────────────────
const TIMELINE_METRIC_ROWS = [
    { key: 'videosAdded', label: 'Videos Added' },
    { key: 'contentHours', label: 'Hours of Content', suffix: 'h' },
    { key: 'subtitlesGenerated', label: 'Subtitles Generated' },
    { key: 'editingHours', label: 'Editing Hours', suffix: 'h' },
    { key: 'textTranslations', label: 'Text Translations' },
    { key: 'dubbingTransformations', label: 'Dubbing Transformations' },
    { key: 'gujaratiWordsTranslated', label: 'Gujarati Words Translated', highlight: true },
];

const TimelineCard = ({ title, period }) => (
    <Paper sx={{ p: 3, height: '100%' }} variant="outlined">
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            {title}
        </Typography>
        {period && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                {new Date(period.periodStart).toLocaleDateString()} – {new Date(period.periodEnd).toLocaleDateString()}
            </Typography>
        )}
        {TIMELINE_METRIC_ROWS.map(({ key, label, suffix, highlight }) => (
            <Box
                key={key}
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Typography variant="body2" color={highlight ? 'text.primary' : 'text.secondary'} sx={{ fontWeight: highlight ? 600 : 400 }}>
                    {label}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: highlight ? 'success.main' : 'text.primary' }}>
                    {period ? `${(period[key] ?? 0).toLocaleString()}${suffix || ''}` : '—'}
                </Typography>
            </Box>
        ))}
    </Paper>
);

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, subtitle, color = 'primary.main' }) => (
    <Card sx={{ height: '100%' }}>
        <CardContent sx={{ textAlign: 'center', py: 3 }}>
            <Box sx={{ color, mb: 1 }}>{icon}</Box>
            <Typography variant="h3" sx={{ fontWeight: 700, color }}>
                {value ?? '—'}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 0.5 }}>
                {label}
            </Typography>
            {subtitle && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {subtitle}
                </Typography>
            )}
        </CardContent>
    </Card>
);

// ─── Gauge Bar ─────────────────────────────────────────────────────────────────
const GaugeBar = ({ label, value, unit = '%', color }) => {
    const numericValue = value != null ? Number(value) : null;
    const barColor = color || (numericValue > 85 ? 'error' : numericValue > 65 ? 'warning' : 'success');
    return (
        <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">{label}</Typography>
                <Typography variant="body2" fontWeight={600}>
                    {numericValue != null ? `${numericValue}${unit}` : 'N/A'}
                </Typography>
            </Box>
            {numericValue != null && (
                <LinearProgress
                    variant="determinate"
                    value={Math.min(numericValue, 100)}
                    color={barColor}
                    sx={{ height: 8, borderRadius: 4 }}
                />
            )}
        </Box>
    );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const ImpactReportPage = ({ darkMode, toggleDarkMode, themeVariant }) => {
    const [period, setPeriod] = useState(30);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [timeline, setTimeline] = useState(null);
    const [timelineLoading, setTimelineLoading] = useState(false);
    const [timelineError, setTimelineError] = useState(null);
    const [timelineElapsedSec, setTimelineElapsedSec] = useState(0);

    // Ticks while the timeline loads so the user can see it's actively working,
    // since an uncached "since start" report can take a while to build the first time.
    useEffect(() => {
        if (!timelineLoading) return;
        setTimelineElapsedSec(0);
        const interval = setInterval(() => setTimelineElapsedSec((s) => s + 1), 1000);
        return () => clearInterval(interval);
    }, [timelineLoading]);

    const loadTimeline = async () => {
        setTimelineLoading(true);
        setTimelineError(null);
        try {
            const data = await ReportService.getImpactTimeline();
            setTimeline(data);
        } catch (err) {
            setTimelineError(err.message || 'Failed to load timeline');
        } finally {
            setTimelineLoading(false);
        }
    };

    useEffect(() => {
        loadTimeline();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const generateReport = async (days = period) => {
        setLoading(true);
        setError(null);
        setReport(null);
        try {
            const data = await ReportService.getImpactReport(days);
            setReport(data);
        } catch (err) {
            setError(err.message || 'Failed to generate report');
        } finally {
            setLoading(false);
        }
    };

    const handlePeriodChange = (days) => {
        setPeriod(days);
        if (report) {
            generateReport(days);
        }
    };

    const handlePrint = () => {
        const now = new Date();
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        const yyyy = now.getFullYear();
        const prevTitle = document.title;
        document.title = `AmrutVaani-DonorImpact-${period}Days-${mm}-${dd}-${yyyy}`;
        window.print();
        document.title = prevTitle;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleString();
    };

    const sys = report?.systemMetrics || {};

    return (
        <AdminPageWrapper
            title="Donor Impact Report"
            darkMode={darkMode}
            toggleDarkMode={toggleDarkMode}
            themeVariant={themeVariant}
        >
            {/* ── Print stylesheet injected inline ── */}
            <style>{`
                @media print {
                    .no-print { display: none !important; }
                    body { background: white !important; }
                }
            `}</style>

            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
                <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Donor Impact Report
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Generate a summary of platform usage and server health to share with server donors.
            </Typography>

            {/* ── Timeline ── */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    <TranslateIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Impact Timeline
                </Typography>
                <Button
                    size="small"
                    startIcon={timelineLoading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
                    onClick={loadTimeline}
                    disabled={timelineLoading}
                    className="no-print"
                >
                    Refresh
                </Button>
            </Box>
            {timelineError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {timelineError}
                </Alert>
            )}
            {timelineLoading && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 4, mb: 3 }}>
                    <CircularProgress size={28} />
                    <Typography variant="body2" color="text.secondary">
                        {timeline
                            ? `Refreshing timeline… (${timelineElapsedSec}s)`
                            : `Building timeline… (${timelineElapsedSec}s) — the first calculation can take a while as monthly totals are cached; future loads will be much faster.`}
                    </Typography>
                </Box>
            )}
            {timeline && (
                <Grid container spacing={2} sx={{ mb: 4, opacity: timelineLoading ? 0.5 : 1 }}>
                    <Grid item xs={12} md={4}>
                        <TimelineCard title="This Year" period={timeline.thisYear} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TimelineCard title="Last Year" period={timeline.lastYear} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TimelineCard title="Since Start" period={timeline.sinceStart} />
                    </Grid>
                </Grid>
            )}

            {/* ── Controls ── */}
            <Paper sx={{ p: 3, mb: 3 }} className="no-print">
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Report Period
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <ButtonGroup variant="outlined">
                        <Button
                            variant={period === 30 ? 'contained' : 'outlined'}
                            onClick={() => handlePeriodChange(30)}
                        >
                            Last 30 Days
                        </Button>
                        <Button
                            variant={period === 90 ? 'contained' : 'outlined'}
                            onClick={() => handlePeriodChange(90)}
                        >
                            Last 90 Days
                        </Button>
                    </ButtonGroup>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                        onClick={() => generateReport(period)}
                        disabled={loading}
                    >
                        {loading ? 'Generating…' : 'Generate Report'}
                    </Button>
                    {report && (
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={handlePrint}
                        >
                            Print / Save as PDF
                        </Button>
                    )}
                </Box>
            </Paper>

            {/* ── Errors ── */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* ── Report Content ── */}
            {report && (
                <Box>
                    {/* Header Meta */}
                    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Generated: {formatDate(report.generatedAt)}
                        </Typography>
                        <Chip
                            label={`Last ${report.periodDays} Days`}
                            color="primary"
                            size="small"
                        />
                    </Box>

                    {/* ── Section 1: Content Stats ── */}
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                        Content Impact
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <StatCard
                                icon={<VideoLibraryIcon sx={{ fontSize: 40 }} />}
                                label="Videos on Platform"
                                value={report.totalVideos}
                                subtitle={`+${report.videosAddedInPeriod} added this period`}
                                color="primary.main"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <StatCard
                                icon={<SubtitlesIcon sx={{ fontSize: 40 }} />}
                                label="Total Subtitle Tracks"
                                value={report.totalCompletedSubtitles}
                                subtitle={`${report.subtitlesGeneratedInPeriod} generated this period`}
                                color="success.main"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <StatCard
                                icon={<LanguageIcon sx={{ fontSize: 40 }} />}
                                label="Languages Supported"
                                value={report.languagesSupported}
                                subtitle={report.languageList?.join(', ')}
                                color="info.main"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <StatCard
                                icon={<AccessTimeIcon sx={{ fontSize: 40 }} />}
                                label="Hours of Content"
                                value={report.totalContentHours != null ? `${report.totalContentHours}h` : '—'}
                                subtitle="total video duration on platform"
                                color="warning.main"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <StatCard
                                icon={<AccessTimeIcon sx={{ fontSize: 40 }} />}
                                label="Editing Hours"
                                value={report.editingHoursInPeriod != null ? `${report.editingHoursInPeriod}h` : '—'}
                                subtitle={`across ${report.editingSessionsInPeriod ?? 0} editing sessions this period`}
                                color="secondary.main"
                            />
                        </Grid>
                    </Grid>

                    {/* ── Text Content stats row ── */}
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={6} md={4}>
                            <StatCard
                                icon={<DescriptionIcon sx={{ fontSize: 40 }} />}
                                label="Text Files"
                                value={report.totalTextFiles}
                                subtitle={`+${report.textFilesInPeriod ?? 0} added this period`}
                                color="primary.main"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <StatCard
                                icon={<LanguageIcon sx={{ fontSize: 40 }} />}
                                label="Text Translations"
                                value={report.totalTextTranslations}
                                subtitle={`${report.textTranslationsInPeriod ?? 0} completed this period`}
                                color="success.main"
                            />
                        </Grid>
                    </Grid>

                    {/* ── Dubbing Transformations stats row ── */}
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={6} md={4}>
                            <StatCard
                                icon={<GraphicEqIcon sx={{ fontSize: 40 }} />}
                                label="Dubbing Transformations"
                                value={report.totalDubbingTransformations ?? 0}
                                subtitle={`${report.dubbingTransformationsInPeriod ?? 0} completed this period`}
                                color="secondary.main"
                            />
                        </Grid>
                    </Grid>

                    {/* ── Section 2: Language Breakdown ── */}
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                        Language Breakdown
                    </Typography>
                    <TableContainer component={Paper} sx={{ mb: 4 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Language</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                                        Subtitle Tracks
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                                        % of Total
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Object.entries(report.languageBreakdown || {}).map(([lang, count]) => {
                                    const total = Object.values(report.languageBreakdown).reduce((a, b) => a + b, 0);
                                    const pct = total > 0 ? ((count / total) * 100).toFixed(1) : '0.0';
                                    return (
                                        <TableRow key={lang} hover>
                                            <TableCell>{lang}</TableCell>
                                            <TableCell align="right">{count}</TableCell>
                                            <TableCell align="right">{pct}%</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* ── Section 3: Server Health ── */}
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                        <StorageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Server Health
                    </Typography>
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700 }}>
                                    CPU
                                </Typography>
                                <GaugeBar label="CPU Usage" value={sys.cpuUsagePercent} />
                                <Typography variant="body2" color="text.secondary">
                                    {sys.availableProcessors} processor{sys.availableProcessors !== 1 ? 's' : ''}
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700 }}>
                                    Memory (RAM)
                                </Typography>
                                <GaugeBar label="Memory Usage" value={sys.memoryUsagePercent} />
                                <Typography variant="body2" color="text.secondary">
                                    {sys.memoryUsedMB?.toLocaleString()} MB used of {sys.memoryMaxMB?.toLocaleString()} MB
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700 }}>
                                    Disk Storage
                                </Typography>
                                <GaugeBar label="Disk Usage" value={sys.diskUsagePercent} />
                                <Typography variant="body2" color="text.secondary">
                                    {sys.diskUsedGB} GB used of {sys.diskTotalGB} GB ({sys.diskFreeGB} GB free)
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Uptime */}
                    <Paper sx={{ p: 2.5, mb: 4, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="body1">
                            <strong>Server Uptime:</strong> {sys.serverUptimeDays} days (
                            {sys.serverUptimeHours?.toLocaleString()} hours since last restart)
                        </Typography>
                        <Chip label="Online" color="success" size="small" />
                    </Paper>

                    {/* ── Section 4: Donated Hardware ── */}
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        <ComputerIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Donated Server Hardware
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        This platform runs on 2 dedicated on-premise servers donated by our supporters, hosted via VMware on physical hardware.
                    </Typography>
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        {[
                            { label: 'AmrutVaani Application Server', uptime: sys.serverUptimeDays != null ? `${sys.serverUptimeDays} days` : '—' },
                            { label: 'AmrutVaani AI Server', uptime: '273+ days' },
                        ].map(({ label, uptime }) => (
                            <Grid item xs={12} md={6} key={label}>
                                <Paper variant="outlined" sx={{ p: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        <ComputerIcon color="primary" />
                                        <Typography variant="subtitle1" fontWeight={700}>{label}</Typography>
                                        <Chip label="Active" color="success" size="small" sx={{ ml: 'auto' }} />
                                    </Box>
                                    {[
                                        { spec: 'CPU', detail: '4 vCPU — Intel Xeon E5-2650 @ 2.0 GHz' },
                                        { spec: 'RAM', detail: '15 GB' },
                                        { spec: 'Storage', detail: '400 GB SSD (300 GB + 100 GB)' },
                                        { spec: 'OS', detail: 'Ubuntu 20.04.6 LTS' },
                                        { spec: 'Type', detail: 'On-premise VMware VM' },
                                        { spec: 'Uptime', detail: uptime },
                                    ].map(({ spec, detail }) => (
                                        <Box key={spec} sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 72 }}>{spec}:</Typography>
                                            <Typography variant="body2" fontWeight={600}>{detail}</Typography>
                                        </Box>
                                    ))}
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>

                    {/* ── Section 5: Cost Comparison ── */}
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        <SavingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Equivalent Cloud Cost (2 Servers)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        If this infrastructure were rented commercially instead of donated, the estimated cost would be:
                    </Typography>
                    <TableContainer component={Paper} sx={{ mb: 3 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Platform</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Instance / Config</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Per Server / mo</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>2 Servers / mo</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Annual Value</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {[
                                    { platform: 'AWS EC2', instance: 'm6i.xlarge + 400 GB gp3 EBS', perServer: '$195', total: '$390', annual: '$4,680' },
                                    { platform: 'Google Cloud', instance: 'n2-standard-4 + 400 GB pd-ssd', perServer: '$185', total: '$370', annual: '$4,440' },
                                    { platform: 'DigitalOcean', instance: 'General 4 vCPU / 16 GB + volumes', perServer: '$155', total: '$310', annual: '$3,720' },
                                    { platform: 'Hetzner', instance: 'CCX33 + 200 GB block volume', perServer: '$65', total: '$130', annual: '$1,560' },
                                ].map((row) => (
                                    <TableRow key={row.platform} hover>
                                        <TableCell sx={{ fontWeight: 600 }}>{row.platform}</TableCell>
                                        <TableCell>{row.instance}</TableCell>
                                        <TableCell align="right">{row.perServer}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>{row.total}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>{row.annual}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Paper sx={{ p: 2.5, mb: 4, backgroundColor: 'success.main', color: 'success.contrastText', borderRadius: 2 }}>
                        <Typography variant="subtitle1" fontWeight={700} align="center">
                            Estimated Annual Donation Value: $3,720 – $4,680
                        </Typography>
                        <Typography variant="body2" align="center" sx={{ mt: 0.5, opacity: 0.9 }}>
                            Based on equivalent cloud pricing for 2 servers with matching specifications (4 vCPU / 15 GB RAM / 400 GB SSD).
                        </Typography>
                    </Paper>

                    {/* ── Footer ── */}
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="body2" color="text.secondary" align="center">
                        This report was generated from live server data on {formatDate(report.generatedAt)}.
                        <br />
                        Thank you to our donors for making this platform possible.
                    </Typography>
                </Box>
            )}

            {/* Empty state */}
            {!report && !loading && !error && (
                <Paper sx={{ p: 6, textAlign: 'center' }}>
                    <AssessmentIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                        Select a period and click <strong>Generate Report</strong> to get started.
                    </Typography>
                </Paper>
            )}
        </AdminPageWrapper>
    );
};

export default ImpactReportPage;
